<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { MarDropdownInput } from '@nimling/components-nimtech'
import type { Page, PageVariable } from '@/types'
import { getSourceRange } from '@/lib/api'
import { stepFor, useRangeStore } from '@/stores/range'
import { useVariablesStore } from '@/stores/variables'
import { variableLabel } from '@/lib/variables'

const props = defineProps<{ page: Page }>()

const rangeStore = useRangeStore()
const variablesStore = useVariablesStore()

const variables = computed<PageVariable[]>(() => (Array.isArray(props.page.variables) ? props.page.variables : []))
const values = computed(() => variablesStore.getVariableValues(props.page.id))
const range = computed(() => rangeStore.range)

watch(
  [() => props.page.id, variables],
  ([id, vars]) => variablesStore.putVariables(id, vars),
  { immediate: true }
)

const queryOptions = ref<Record<string, string[]>>({})

const getQueryRange = () => {
  if (range.value) return range.value
  const to = Date.now()
  const from = to - 24 * 60 * 60_000
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString(), step: stepFor(from, to) }
}

const queryKey = computed(() => {
  const queries = variables.value.filter(v => v.kind === 'query' && v.query?.source_id && v.query?.column)
  return `${props.page.id}|${range.value?.from ?? 'live'}|${range.value?.to ?? ''}|${queries.map(q => `${q.name}:${q.query!.source_id}:${q.query!.column}`).join(',')}`
})

watch(
  queryKey,
  () => {
    const r = getQueryRange()
    for (const v of variables.value) {
      if (v.kind !== 'query' || !v.query?.source_id || !v.query?.column) continue
      const { source_id, column } = v.query
      getSourceRange(source_id, r)
        .then(res => {
          const seen = new Set<string>()
          for (const row of res.rows) {
            const raw = row[column] ?? (row.log as Record<string, unknown> | undefined)?.[column]
            if (raw == null || raw === '') continue
            seen.add(String(raw))
          }
          queryOptions.value = { ...queryOptions.value, [v.name]: [...seen].sort() }
        })
        .catch(() => { queryOptions.value = { ...queryOptions.value, [v.name]: [] } })
    }
  },
  { immediate: true }
)

const getOptions = (v: PageVariable) => {
  const base = v.kind === 'query' ? (queryOptions.value[v.name] ?? []) : (v.options ?? [])
  const picked = values.value[v.name] ?? []
  return [...new Set([...base, ...picked])].map(o => ({ label: o, value: o }))
}

const onPick = (v: PageVariable, picked: string | string[] | null) => {
  if (Array.isArray(picked)) variablesStore.putVariable(props.page.id, v.name, picked)
  else variablesStore.putVariable(props.page.id, v.name, picked ? [picked] : [])
}
</script>

<template>
  <div v-if="variables.length > 0" class="variable-bar" role="group" aria-label="Page variables">
    <template v-for="v in variables" :key="v.name">
      <MarDropdownInput
        v-if="v.multi"
        icon="filter"
        :placeholder="variableLabel(v)"
        :model-value="values[v.name] ?? []"
        :options="getOptions(v)"
        :select-min="0"
        searchable
        @update:model-value="onPick(v, $event)"
      />
      <MarDropdownInput
        v-else
        icon="filter"
        :placeholder="variableLabel(v)"
        :model-value="values[v.name]?.[0] ?? null"
        :options="getOptions(v)"
        show-clear
        :searchable="v.kind !== 'interval'"
        @update:model-value="onPick(v, $event)"
      />
    </template>
  </div>
</template>

<style scoped>
.variable-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  min-width: 0;
}
</style>
